import { useEffect } from "react";
import type { NextRouter } from "next/router";

const ACCOUNT_ROUTE_SECTIONS = new Set([
	"branding",
	"data",
	"groups",
	"maps",
	"settings",
	"sign-in",
	"support",
	"users",
]);

const getAccountRoute = (
	url: string,
): { isCustomerContent: boolean; slug: string } | undefined => {
	const path = url.split(/[?#]/, 1)[0] ?? "";
	const [, slug, section, resourceId, resourceName, ...remainingSegments] =
		path.split("/");
	if (!slug || !section || !ACCOUNT_ROUTE_SECTIONS.has(section))
		return undefined;

	const isPortal = (section === "maps" || section === "data") && !resourceId;
	const isCanonicalResource = Boolean(
		(section === "maps" || section === "data") &&
			resourceId &&
			resourceName &&
			resourceName !== "settings" &&
			resourceName !== "tools" &&
			remainingSegments.length === 0,
	);

	return { isCustomerContent: isPortal || isCanonicalResource, slug };
};

export const useAccountBoundaryNavigation = (
	router: NextRouter,
	customerAnalyticsActive: boolean,
): void => {
	useEffect(() => {
		const currentAccountSlug = router.query["account-name"];
		const normalizedCurrentSlug = Array.isArray(currentAccountSlug)
			? currentAccountSlug[0]
			: currentAccountSlug;
		if (!normalizedCurrentSlug) return;

		const forceDocumentNavigation = (url: string): boolean => {
			if (!customerAnalyticsActive) return false;
			const destination = getAccountRoute(url);
			if (
				destination?.slug === normalizedCurrentSlug &&
				destination.isCustomerContent
			) {
				return false;
			}

			window.location.assign(url);
			return true;
		};

		const handleRouteChangeStart = (url: string) => {
			if (!forceDocumentNavigation(url)) return;
			const cancellationError = new Error(
				"Customer analytics isolation requires a document reload",
			) as Error & { cancelled?: boolean };
			cancellationError.cancelled = true;
			throw cancellationError;
		};

		router.events.on("routeChangeStart", handleRouteChangeStart);
		router.beforePopState(({ as }) => !forceDocumentNavigation(as));

		return () => {
			router.events.off("routeChangeStart", handleRouteChangeStart);
			router.beforePopState(() => true);
		};
	}, [customerAnalyticsActive, router]);
};
